/**
 * Notifications — live API.
 * CRUD for notification rules via /v1/system/settings/notification-rules.
 *
 * P0.1 Path A3 cutover (2026-05-24): the legacy "IM webhooks" tab was
 * removed. IM channel install (Slack / Teams) now lives
 * under /integrations/connectors with OAuth2 + per-event
 * subscriptions. See svdy-platform/IM_INTEGRATION_ARCHITECTURE.md.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Typography, Button, IconButton, Stack, Tooltip, Chip, TextField, TablePagination,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Snackbar,
} from '@mui/material';
import {
  Add as AddIcon, RefreshOutlined, EditOutlined, DeleteOutlined,
  EmailOutlined, LockOutlined,
} from '@mui/icons-material';
import { ApiError, systemApi } from '../../../api';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../i18n/translateApiError';
import { ConfirmDialog } from '../../../components/primitives';
import { useNavigate } from 'react-router-dom';
import {
  digestsApi,
  type DigestSubscriptionResponse,
  type DigestCadence,
  type DigestReportType,
} from '../../../api/notifications';
import { currentApi } from '../../../api/subscription';
import { activeLocale } from '../../../utils/datetime';

const TIER_RANK: Record<string, number> = {
  free: 0, starter: 1, professional: 2, enterprise: 3,
};
const tierAtLeast = (actual: string | null | undefined, required: string): boolean =>
  (TIER_RANK[actual ?? ''] ?? -1) >= (TIER_RANK[required] ?? 99);

// Subset shown in the Add dialog — every Pro+ admin can subscribe to
// any of these. attendance_worker_personal is also worker-accessible
// and surfaces in the worker portal's /me/notifications page.
const ADMIN_ADDABLE_TYPES: DigestReportType[] = [
  'attendance_admin',
  'attendance_worker_personal',
  'process_admin',
];

interface Rule { id: string; name: string; event_type: string; enabled: boolean; created_at?: string | null; }

// Event-type catalogue for the dropdown. These are the ONLY events the
// backend actually dispatches (app/integrations/dispatcher.py KNOWN_EVENTS).
// A rule's event_type must be one of these or it can never fire — the
// 2026-06-16 overhaul aligned this list with the real dispatch vocabulary
// and wired dispatch_event to honor each rule's `enabled` master switch.
const EVENT_CODES: string[] = [
  'clock_in_late',
  'leave_request_submitted',
  'leave_request_approved',
  'leave_request_rejected',
  'process_request_submitted',
  'process_request_status_changed',
  'shift_assigned',
];
const EVENT_CODE_SET = new Set(EVENT_CODES);

const humaniseEvent = (code: string, t: (k: string) => string): string => {
  if (EVENT_CODE_SET.has(code)) return t(`system:notifications.events.${code}`);
  return code
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

interface FormState { name: string; event_type: string; enabled: boolean; }
const EMPTY: FormState = { name: '', event_type: '', enabled: true };

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Rule | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [toDelete, setToDelete] = useState<Rule | null>(null);

  // P0.3: digest subscriptions state
  const [digests, setDigests] = useState<DigestSubscriptionResponse[]>([]);
  const [digestsLoading, setDigestsLoading] = useState(false);
  const [orgTier, setOrgTier] = useState<string | null>(null);
  const isPro = tierAtLeast(orgTier, 'professional');

  // Add-subscription dialog state
  const [digestDialogOpen, setDigestDialogOpen] = useState(false);
  const [digestForm, setDigestForm] = useState<{
    report_type: DigestReportType;
    cadence: DigestCadence;
  }>({ report_type: 'attendance_admin', cadence: 'daily' });

  const fetchDigests = useCallback(async () => {
    setDigestsLoading(true);
    try { setDigests(await digestsApi.list()); } catch { /* empty */ }
    finally { setDigestsLoading(false); }
  }, []);

  useEffect(() => { fetchDigests(); }, [fetchDigests]);
  useEffect(() => {
    let alive = true;
    currentApi.get().then((sub) => {
      if (!alive) return;
      const tier = (sub as { plan?: { tier?: string } | null } | null)?.plan?.tier ?? null;
      setOrgTier(tier);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const handleAddDigest = async (
    report_type: DigestReportType, cadence: DigestCadence,
  ) => {
    try {
      await digestsApi.create({ report_type, cadence });
      setToast(t('system:notifications.digests.toastSubscribed', { report: t(`system:notifications.digests.reports.${report_type}`), cadence }));
      setDigestDialogOpen(false);
      fetchDigests();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('system:notifications.digests.failedToSubscribe'));
    }
  };

  const openDigestDialog = () => {
    // Default to the first not-yet-subscribed type for convenience.
    const subscribed = new Set(digests.map((d) => d.report_type));
    const firstAvailable = ADMIN_ADDABLE_TYPES.find((rt) => !subscribed.has(rt))
      ?? ADMIN_ADDABLE_TYPES[0];
    setDigestForm({ report_type: firstAvailable, cadence: 'daily' });
    setDigestDialogOpen(true);
  };

  const handleToggleDigestEnabled = async (
    sub: DigestSubscriptionResponse,
  ) => {
    try {
      await digestsApi.update(sub.id, { enabled: !sub.enabled });
      fetchDigests();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('system:notifications.failedToToggle'));
    }
  };

  const handleChangeCadence = async (
    sub: DigestSubscriptionResponse, cadence: DigestCadence,
  ) => {
    try {
      await digestsApi.update(sub.id, { cadence });
      fetchDigests();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('system:notifications.digests.failedToUpdateCadence'));
    }
  };

  const handleDeleteDigest = async (sub: DigestSubscriptionResponse) => {
    try {
      await digestsApi.delete(sub.id);
      setToast(t('system:notifications.digests.toastRemoved'));
      fetchDigests();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('system:notifications.digests.failedToDelete'));
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try { setRules(await systemApi.notificationsApi.list() as Rule[]); } catch { /* empty */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(
    () => rules.filter(r =>
      r.name.toLowerCase().includes(filter.toLowerCase())
      || humaniseEvent(r.event_type, t).toLowerCase().includes(filter.toLowerCase())
    ),
    [rules, filter, t],
  );
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (r: Rule) => {
    setEditing(r);
    setForm({ name: r.name, event_type: r.event_type, enabled: r.enabled });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.event_type) return;
    setSaving(true);
    try {
      if (editing) {
        await systemApi.notificationsApi.update(editing.id, form);
        setToast(t('system:notifications.toastUpdated', { name: form.name }));
      } else {
        await systemApi.notificationsApi.create(form);
        setToast(t('system:notifications.toastCreated', { name: form.name }));
      }
      setDialogOpen(false);
      await fetchAll();
    } catch (e) { setToast(e instanceof ApiError ? translateApiError(e, t) : t('system:shared.failedToSave')); }
    finally { setSaving(false); }
  };

  const handleToggle = async (r: Rule) => {
    try {
      await systemApi.notificationsApi.update(r.id, { enabled: !r.enabled });
      setRules(prev => prev.map(x => x.id === r.id ? { ...x, enabled: !x.enabled } : x));
    } catch { setToast(t('system:notifications.failedToToggle')); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await systemApi.notificationsApi.delete(toDelete.id);
      setToDelete(null);
      setToast(t('system:notifications.toastDeleted'));
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('system:notifications.digests.failedToDelete'));
      throw e;
    }
  };

  if (loading && rules.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
      py: '12px', width: '100%',
    }}>
      <Box sx={{
        width: '100%', display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', gap: 1.5, mb: 1.5,
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {t('system:notifications.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('system:notifications.subtitle')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('system:shared.refresh')}>
            <IconButton size="small" onClick={fetchAll} sx={{
              border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1,
              bgcolor: '#fff', '&:hover': { bgcolor: '#f5f5f5' },
            }}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>
            {t('system:notifications.addRule')}
          </Button>
        </Stack>
      </Box>

      {/* P0.1 Path A3 hint banner — point users to the new home for IM webhooks.
          2026-05-25 cleanup (Bug 12 + Bug 15a): added a direct link button to Connectors. */}
      <Alert
        severity="info"
        variant="outlined"
        icon={false}
        sx={{ width: '100%', mb: 2, fontSize: '0.8rem' }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => navigate('/integrations/connectors')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              whiteSpace: 'nowrap',
              alignSelf: 'center',
            }}
          >
            {t('system:notifications.openConnectors')}
          </Button>
        }
      >
        <span dangerouslySetInnerHTML={{ __html: t('system:notifications.imBanner') }} />
      </Alert>

      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 1.5, gap: 1.5 }}>
        <TextField size="small" label={t('system:notifications.filterLabel')} value={filter}
          onChange={e => { setFilter(e.target.value); setPage(0); }} sx={{ width: 300 }} />
        <Box sx={{ flex: 1 }} />
        <TablePagination component="div" count={filtered.length} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 } }} />
      </Box>

      <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
            <TableRow>
              <TableCell>{t('system:notifications.tableHeaders.name')}</TableCell>
              <TableCell>{t('system:notifications.tableHeaders.event')}</TableCell>
              <TableCell align="center">{t('system:notifications.tableHeaders.enabled')}</TableCell>
              <TableCell align="center">{t('system:notifications.tableHeaders.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {filter ? t('system:notifications.noMatch') : t('system:notifications.noRules')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginated.map(r => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={humaniseEvent(r.event_type, t)} size="small" variant="outlined"
                    sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }} title={r.event_type} />
                </TableCell>
                <TableCell align="center">
                  <Switch size="small" checked={r.enabled} onChange={() => handleToggle(r)} />
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <IconButton size="small" onClick={() => openEdit(r)}>
                      <EditOutlined sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => setToDelete(r)}
                      sx={{ '&:hover': { color: 'error.main' } }}>
                      <DeleteOutlined sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* P0.3 Email Digests section */}
      <Box sx={{ width: '100%', mt: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <EmailOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            {t('system:notifications.digests.title')}
          </Typography>
          {!isPro && (
            <Chip
              icon={<LockOutlined sx={{ fontSize: 12 }} />}
              label={t('system:notifications.digests.proBadge')}
              size="small"
              sx={{ height: 20, fontSize: '0.65rem', borderRadius: 0.75 }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {isPro && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={openDigestDialog}
              sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.8rem' }}
            >
              {t('system:notifications.digests.addSubscription')}
            </Button>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('system:notifications.digests.subtitle')}
          {!isPro && (
            <> {t('system:notifications.digests.upgradeHint')}</>
          )}
        </Typography>

        {digestsLoading ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : digests.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              textAlign: 'center',
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('system:notifications.digests.noSubscriptions')}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {isPro
                ? t('system:notifications.digests.addHint')
                : t('system:notifications.digests.proRequired')}
            </Typography>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            {digests.map((sub, idx) => (
              <Box
                key={sub.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1.5,
                  gap: 1.5,
                  borderTop: idx === 0 ? 'none' : '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {t(`system:notifications.digests.reports.${sub.report_type}`, { defaultValue: sub.report_type })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {sub.last_sent_at
                      ? t('system:notifications.digests.lastSent', { date: new Date(sub.last_sent_at).toLocaleString(activeLocale()) })
                      : t('system:notifications.digests.notYetSent')}
                  </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 110 }} disabled={!isPro}>
                  <Select
                    value={sub.cadence}
                    onChange={(e) =>
                      handleChangeCadence(sub, e.target.value as DigestCadence)
                    }
                    sx={{ fontSize: '0.85rem', height: 32 }}
                  >
                    <MenuItem value="daily">{t('system:notifications.digests.daily')}</MenuItem>
                    <MenuItem value="weekly">{t('system:notifications.digests.weekly')}</MenuItem>
                  </Select>
                </FormControl>
                <Switch
                  checked={sub.enabled}
                  onChange={() => handleToggleDigestEnabled(sub)}
                  size="small"
                  disabled={!isPro}
                />
                <IconButton
                  size="small"
                  onClick={() => handleDeleteDigest(sub)}
                  disabled={!isPro}
                  sx={{ '&:hover': { color: 'error.main' } }}
                >
                  <DeleteOutlined sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
          </Paper>
        )}
      </Box>

      {/* Digest add subscription dialog (P0.3) */}
      <Dialog
        open={digestDialogOpen}
        onClose={() => setDigestDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ py: 2 }}>{t('system:notifications.digests.dialogTitle')}</DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('system:notifications.digests.reportLabel')}</InputLabel>
              <Select
                label={t('system:notifications.digests.reportLabel')}
                value={digestForm.report_type}
                onChange={(e) =>
                  setDigestForm((f) => ({
                    ...f,
                    report_type: e.target.value as DigestReportType,
                  }))
                }
              >
                {ADMIN_ADDABLE_TYPES.map((rt) => (
                  <MenuItem key={rt} value={rt}>
                    {t(`system:notifications.digests.reports.${rt}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{t('system:notifications.digests.cadenceLabel')}</InputLabel>
              <Select
                label={t('system:notifications.digests.cadenceLabel')}
                value={digestForm.cadence}
                onChange={(e) =>
                  setDigestForm((f) => ({
                    ...f,
                    cadence: e.target.value as DigestCadence,
                  }))
                }
              >
                <MenuItem value="daily">{t('system:notifications.digests.cadenceDaily')}</MenuItem>
                <MenuItem value="weekly">{t('system:notifications.digests.cadenceWeekly')}</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              {t('system:notifications.digests.dialogHint')}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button
            onClick={() => setDigestDialogOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            {t('system:shared.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              handleAddDigest(digestForm.report_type, digestForm.cadence)
            }
            sx={{ textTransform: 'none' }}
          >
            {t('system:notifications.digests.subscribe')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 2 }}>
          {editing ? t('system:notifications.dialog.editTitle') : t('system:notifications.dialog.addTitle')}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('system:notifications.dialog.nameLabel')} required value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>{t('system:notifications.dialog.eventTypeLabel')}</InputLabel>
                <Select value={form.event_type} label={t('system:notifications.dialog.eventTypeLabel')}
                  onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))}>
                  {EVENT_CODES.map(code => (
                    <MenuItem key={code} value={code}>{t(`system:notifications.events.${code}`)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t('system:shared.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ textTransform: 'none' }}>
            {saving ? <CircularProgress size={18} /> : editing ? t('system:shared.save') : t('system:shared.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={!!toDelete} tone="danger" title={t('system:notifications.deleteTitle')}
        description={t('system:notifications.deleteDescription')}
        subject={toDelete && <Typography sx={{ fontWeight: 600 }}>{toDelete.name}</Typography>}
        confirmLabel={t('common:actions.delete')} confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleDelete} onClose={() => setToDelete(null)} />
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Notifications;
